/**
 * Aspect — Shared auth page chrome (dark architectural theme)
 */

import { t, langToggle } from "../i18n.js";

export function authLayout({ title, subtitle, childrenHtml, footerHtml = "" }) {
  return `
    <div class="portal-auth">
      ${langToggle("ap-lang--float")}
      <aside class="portal-auth__hero" aria-hidden="true">
        <div class="portal-auth__hero-inner">
          <div class="portal-auth__logo">Aspect</div>
          <p class="portal-auth__hero-tag">${t("Creator Studio")}</p>
          <h2 class="portal-auth__hero-title">${t("Stage apartments in 3D.<br />Share walkable tours.")}</h2>
          <p class="portal-auth__hero-copy">
            ${t("Trace floor plans, furnish with real catalog items, and publish a link your clients can explore in first person.")}
          </p>
          <ul class="portal-auth__hero-list">
            <li>${t("2D plan editor → live 3D sync")}</li>
            <li>${t("AI furniture staging")}</li>
            <li>${t("Shareable tour links")}</li>
          </ul>
          <div class="portal-auth__hero-grid" aria-hidden="true"></div>
        </div>
      </aside>
      <main class="portal-auth__main">
        <div class="portal-auth__card">
          <header class="portal-auth__head">
            <h1 class="portal-auth__title">${title}</h1>
            ${subtitle ? `<p class="portal-auth__subtitle">${subtitle}</p>` : ""}
          </header>
          ${childrenHtml}
          ${footerHtml ? `<footer class="portal-auth__foot">${footerHtml}</footer>` : ""}
        </div>
        <p class="portal-auth__legal">© ${new Date().getFullYear()} Aspect · ${t("Spatial staging platform")}</p>
      </main>
    </div>
  `;
}

export function field({ id, label, type = "text", placeholder = "", autocomplete = "", value = "", error = "" }) {
  return `
    <div class="portal-field ${error ? "portal-field--error" : ""}">
      <label class="portal-label" for="${id}">${label}</label>
      <input
        class="portal-input"
        id="${id}"
        name="${id}"
        type="${type}"
        placeholder="${placeholder}"
        autocomplete="${autocomplete}"
        value="${escapeAttr(value)}"
      />
      ${error ? `<p class="portal-field__error" role="alert">${error}</p>` : ""}
    </div>
  `;
}

export function btnPrimary(label, { type = "button", id = "", full = true } = {}) {
  return `<button type="${type}" class="portal-btn portal-btn--primary ${full ? "portal-btn--full" : ""}" ${id ? `id="${id}"` : ""}>${label}</button>`;
}

export function btnGhost(label, { href = "#", id = "" } = {}) {
  return `<a class="portal-btn portal-btn--ghost" href="${href}" ${id ? `id="${id}"` : ""}>${label}</a>`;
}

export function alertBox(message, kind = "error") {
  if (!message) return "";
  return `<div class="portal-alert portal-alert--${kind}" role="alert">${escapeHtml(message)}</div>`;
}

export function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function escapeAttr(s) {
  return escapeHtml(s).replace(/'/g, "&#39;");
}

export function bindForm(root, onSubmit) {
  const form = root.querySelector("form");
  if (!form) return;
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const data = new FormData(form);
    const values = Object.fromEntries(data.entries());
    onSubmit(values, form);
  });
}

export function setFieldError(root, fieldId, message) {
  const input = root.querySelector(`#${fieldId}`);
  if (!input) return;
  const wrap = input.closest(".portal-field");
  if (!wrap) return;
  wrap.classList.toggle("portal-field--error", Boolean(message));
  let err = wrap.querySelector(".portal-field__error");
  if (message) {
    if (!err) {
      err = document.createElement("p");
      err.className = "portal-field__error";
      err.setAttribute("role", "alert");
      wrap.appendChild(err);
    }
    err.textContent = message;
  } else if (err) err.remove();
}
